import Vue from "vue";
import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";

import { DIContainer } from "@/app.container";
import { Dictionary } from "@/lib/Dictionary.type";
import dataStore from "@/store";

import QaAsset from "../qa-asset.model";
import QaAssetType from "../qa-asset-type";
import { QaPhotoAngle } from "../qa-photo-angle";

const name = "QualityInspectionStore";

if ((dataStore.state as any)[name]) {
  dataStore.unregisterModule(name);
}

@Module({ name, dynamic: true, store: dataStore })
export default class QualityInspectionStore extends VuexModule {
  qaAsset: Dictionary<QaAsset> = {};

  plushieQaAsset: Dictionary<string[]> = {};

  // ################################### QA ASSETS #########################################

  get getQaAssetById(): (id: string) => QaAsset | undefined {
    return (id: string) => this.qaAsset[id];
  }

  get getQaAssetsByPlushieId(): (plushieId: string) => QaAsset[] {
    return (plushieId: string) => {
      const qaAssetsIds = this.plushieQaAsset[plushieId];

      if (!qaAssetsIds) {
        return [];
      }

      const result: QaAsset[] = [];

      qaAssetsIds.forEach((id) => {
        const image = this.qaAsset[id];
        if (!image) {
          return;
        }

        result.push(image);
      });

      result.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

      return result;
    };
  }

  get getLastGroupQaAssetsByPlushieId(): (plushieId: string) => QaAsset[] {
    return (plushieId: string) => {
      const qaAssetsIds = this.plushieQaAsset[plushieId];

      if (!qaAssetsIds) {
        return [];
      }

      let result: QaAsset[] = [];

      let maxGroupId = 0;

      qaAssetsIds.forEach((id) => {
        const image = this.qaAsset[id];
        if (!image) {
          return;
        }

        if (image.groupNumber > maxGroupId) {
          maxGroupId = image.groupNumber;
        }

        result.push(image);
      });

      result = result.filter((image) => image.groupNumber === maxGroupId);

      result.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

      return result;
    };
  }

  @Mutation
  removeQaAsset(payload: QaAsset) {
    const plushieQaAssets = this.plushieQaAsset[payload.plushie];
    let index = -1;

    if (plushieQaAssets) {
      index = plushieQaAssets.indexOf(payload.id);
    }

    if (index !== -1) {
      plushieQaAssets.splice(index, 1);
    }

    delete this.qaAsset[payload.id];
  }

  @Mutation
  updateQaAsset(payload: QaAsset) {
    Vue.set(this.qaAsset, payload.id, payload);

    if (this.plushieQaAsset[payload.plushie] === undefined) {
      return;
    }

    if (!this.plushieQaAsset[payload.plushie].includes(payload.id)) {
      this.plushieQaAsset[payload.plushie].push(payload.id);
    }
  }

  @Mutation
  updateQaAssets({
    plushieId,
    qaAssets,
  }: {
    plushieId: string;
    qaAssets: QaAsset[];
  }) {
    qaAssets.forEach((qaAsset) => {
      if (qaAsset.plushie !== plushieId) {
        throw new Error(
          "All qa assets should belong to the specified plushie!"
        );
      }
    });

    const plushieQaAssets: string[] = [];

    qaAssets.forEach((qaAsset) => {
      plushieQaAssets.push(qaAsset.id);
      Vue.set(this.qaAsset, qaAsset.id, qaAsset);
    });

    Vue.set(this.plushieQaAsset, plushieId, plushieQaAssets);
  }

  @Action({ rawError: true })
  async createQaAsset({
    id,
    plushie,
    type,
    storageItem,
    angle,
    thumbnailStorageItemId,
  }: {
    id: string;
    plushie: string;
    type: QaAssetType;
    storageItem: string;
    angle?: QaPhotoAngle;
    thumbnailStorageItemId?: string;
  }): Promise<QaAsset> {
    const item = await DIContainer.QaAssetRepository.createQaAsset(
      id,
      plushie,
      type,
      storageItem,
      angle,
      thumbnailStorageItemId
    );

    this.updateQaAsset(item);

    return item;
  }

  @Action({ rawError: true })
  async deleteQaAsset(qaAsset: QaAsset): Promise<void> {
    await DIContainer.QaAssetRepository.delete(qaAsset);

    this.removeQaAsset(qaAsset);

    return;
  }

  @Action({ rawError: true })
  async loadQaAssetsByPlushieId({
    plushieId,
    useCache = true,
  }: {
    plushieId: string;
    useCache?: boolean;
  }) {
    let qaAssets: QaAsset[] = [];

    if (useCache && this.plushieQaAsset[plushieId]) {
      const qaAssetsIds = this.plushieQaAsset[plushieId];

      qaAssetsIds.forEach((id) => {
        qaAssets.push(this.qaAsset[id]);
      });

      return qaAssets;
    }

    qaAssets = await DIContainer.QaAssetRepository.getByPlushieId(plushieId);

    this.updateQaAssets({ plushieId, qaAssets });

    return qaAssets;
  }
}
